import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { DashboardInfoStatisticForAdminResponse, Feature, PlanPeriodDto, ProductIds } from 'core'
import { W2ReportNotificationYear } from 'core/application/commons/config'

import { Button } from 'common/components'
import { formatYearV2, parseZoneFormat } from 'common/dateFormatters'
import { useFeatureFlag, useLoginUserV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import ReportNotification from './ReportNotification'
import TotalReimbursementAmount from './TotalReimbursementAmount'

type NotificationsProps = {
  planPeriods: PlanPeriodDto[]
  statisticInfo: DashboardInfoStatisticForAdminResponse
  planPeriodId: string
}

const Notifications = ({ planPeriods, statisticInfo, planPeriodId }: NotificationsProps) => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const { user } = useLoginUserV2()

  const productId = user?.productId

  const isQsehra = productId === ProductIds.QsehraId

  const canShowCReports = useFeatureFlag(Feature.C1095Report)
  const canShowW2ReportsFlag = useFeatureFlag(Feature.PastYearW2Report)
  const canShowW2Reports =
    canShowW2ReportsFlag &&
    isQsehra &&
    planPeriods?.some((x) => formatYearV2(x.startDate) === W2ReportNotificationYear.toString())
  const planPeriod = planPeriods?.find((x) => x.id == planPeriodId)
  const newPlanPeriod = planPeriods
    ? planPeriods?.find((x) => x.id == statisticInfo?.previousPlanPeriodDetails?.previousPlanPeriodId)
    : null

  return (
    <div>
      {statisticInfo?.totalReimbursementAmount === 0 &&
        statisticInfo?.totalReimbursementRequests === 0 &&
        !user.companyIsAle &&
        !canShowW2Reports && <p className={'t-no-reimbursement-amount'}>Good work. You’re all caught up. 👍</p>}

      {canShowW2Reports && (
        <ReportNotification
          text={`Your ${W2ReportNotificationYear} W-2 Report is ready to download`}
          route={`${ROUTE_PATHS.REPORTS}`}
        />
      )}
      {canShowCReports && user.companyIsAle && (
        <ReportNotification
          text={`Your annual Form 1095 system report is available for download.`}
          route={`${ROUTE_PATHS.REPORTS}/1095-c-reports`}
        />
      )}
      <TotalReimbursementAmount
        totalReimbursementAmount={statisticInfo?.totalReimbursementAmount}
        totalReimbursementDue={statisticInfo?.totalReimbursementDue}
        productId={productId}
        planPeriod={planPeriod}
      />
      {statisticInfo?.previousPlanPeriodDetails && planPeriods && (
        <TotalReimbursementAmount
          productId={productId}
          planPeriod={planPeriods.find((x) => x.id == statisticInfo?.previousPlanPeriodDetails.previousPlanPeriodId)}
          totalReimbursementAmount={statisticInfo?.previousPlanPeriodDetails.totalReimbursementAmount}
          totalReimbursementDue={statisticInfo?.previousPlanPeriodDetails.totalReimbursementDue}
        />
      )}
      {statisticInfo?.totalReimbursementRequests > 0 && planPeriod && (
        <div className="flex flex_space_between outer-div">
          <div className="color-gray-8 pt-4">
            {statisticInfo?.totalReimbursementRequests > 1 ? (
              <span className="color-gray-9 text-semi-bold">
                {statisticInfo?.totalReimbursementRequests} Reimbursement Requests{' '}
              </span>
            ) : (
              <span className="color-gray-9 text-semi-bold">
                {statisticInfo?.totalReimbursementRequests} Reimbursement Request{' '}
              </span>
            )}
            {translate('admin-dashboard.home.perks-welcome.waiting-approval')} for{' '}
            <span className={'color-gray-9 text-semi-bold'}>
              {parseZoneFormat(planPeriod.startDate) + ' - Continuous'}
            </span>
          </div>
          <Button
            className="c-white"
            type="primary"
            onClick={() =>
              history.push(
                `${ROUTE_PATHS.REIMBURSEMENTS}?tabActiveKey=expense-approvals-due&planPeriodId=${planPeriodId}`,
              )
            }
          >
            Approve now
          </Button>
        </div>
      )}

      {statisticInfo?.previousPlanPeriodDetails?.totalReimbursementRequests > 0 && newPlanPeriod && (
        <div className="flex flex_space_between outer-div">
          <div className="color-gray-8 pt-4">
            {statisticInfo?.previousPlanPeriodDetails.totalReimbursementRequests > 1 ? (
              <span className="color-gray-9 text-semi-bold">
                {statisticInfo?.previousPlanPeriodDetails.totalReimbursementRequests} Reimbursement Requests{' '}
              </span>
            ) : (
              <span className="color-gray-9 text-semi-bold">
                {statisticInfo?.previousPlanPeriodDetails.totalReimbursementRequests} Reimbursement Request{' '}
              </span>
            )}
            {translate('admin-dashboard.home.perks-welcome.waiting-approval')} for{' '}
            <span className={'color-gray-9 text-semi-bold'}>
              {parseZoneFormat(newPlanPeriod.startDate) + ' - ' + parseZoneFormat(newPlanPeriod.endDate)}
            </span>
          </div>
          <Button
            className="c-white"
            type="primary"
            onClick={() =>
              history.push(
                `${ROUTE_PATHS.REIMBURSEMENTS}?tabActiveKey=expense-approvals-due&planPeriodId=${newPlanPeriod.id}`,
              )
            }
          >
            Approve now
          </Button>
        </div>
      )}
    </div>
  )
}

export default Notifications
